/**
 * Texture Manager - Handles texture loading and WebGPU texture creation
 */

// Default texture size (1x1 black pixel)
const DEFAULT_TEXTURE_SIZE = 1;

// Texture constraints
const MIN_TEXTURE_SIZE = 1;
const MAX_TEXTURE_SIZE = 4096;
const MAX_CHANNEL = 3;
const CAMERA_TIMEOUT_MS = 15000;

export class TextureManager {
  constructor(device) {
    this.device = device;
    this.textures = new Map(); // channel -> { texture, view, source }
    this.sampler = null;
    this.defaultTexture = null;
    this.defaultTextureView = null;

    // Camera state
    this.cameras = new Map(); // channel -> { video, stream, facingMode, isIOS, rotation, rotationCanvas, rotationCtx }

    // iOS detection (iPad may identify as Mac, so also check touch support)
    this.isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

    // Bound handler for orientationchange
    this._onOrientationChange = this._handleOrientationChange.bind(this);

    // Callbacks
    this.onTextureChange = null;
  }

  /**
   * Validate channel index (0-3)
   * @param {number} channel
   * @returns {{valid: boolean, error?: string}}
   */
  validateChannel(channel) {
    if (typeof channel !== 'number' || !Number.isInteger(channel)) {
      return { valid: false, error: `Invalid channel type: expected integer` };
    }
    if (channel < 0 || channel > MAX_CHANNEL) {
      return { valid: false, error: `Channel ${channel} out of range (0-${MAX_CHANNEL})` };
    }
    return { valid: true };
  }

  /**
   * Clamp texture size to valid range
   * @param {number} size
   * @returns {number}
   */
  clampTextureSize(size) {
    return Math.max(MIN_TEXTURE_SIZE, Math.min(MAX_TEXTURE_SIZE, size || 256));
  }

  /**
   * Initialize the texture manager
   */
  init() {
    // Create shared sampler
    this.sampler = this.device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      mipmapFilter: 'linear',
      addressModeU: 'repeat',
      addressModeV: 'repeat',
    });

    // Create default 1x1 black texture
    this.defaultTexture = this.device.createTexture({
      size: [DEFAULT_TEXTURE_SIZE, DEFAULT_TEXTURE_SIZE],
      format: 'rgba8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });

    // Fill with black pixel
    const blackPixel = new Uint8Array([0, 0, 0, 255]);
    this.device.queue.writeTexture(
      { texture: this.defaultTexture },
      blackPixel,
      { bytesPerRow: 4 },
      [DEFAULT_TEXTURE_SIZE, DEFAULT_TEXTURE_SIZE]
    );

    this.defaultTextureView = this.defaultTexture.createView();
  }

  /**
   * Load texture from an image file
   * @param {File} file - Image file
   * @param {number} channel - Channel index (0-3)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async loadFromFile(file, channel = 0) {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File is not an image' };
    }

    try {
      const bitmap = await createImageBitmap(file);
      return this.createTextureFromBitmap(bitmap, channel, file.name);
    } catch (error) {
      return { success: false, error: `Failed to load image: ${error.message}` };
    }
  }

  /**
   * Load texture from URL
   * @param {string} url - Image URL
   * @param {number} channel - Channel index (0-3)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async loadFromUrl(url, channel = 0) {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      return this.createTextureFromBitmap(bitmap, channel, url);
    } catch (error) {
      return { success: false, error: `Failed to load image: ${error.message}` };
    }
  }

  /**
   * Create texture from ImageBitmap
   * @param {ImageBitmap} bitmap
   * @param {number} channel
   * @param {string} sourceName
   * @returns {{success: boolean, error?: string}}
   */
  createTextureFromBitmap(bitmap, channel, sourceName = 'unknown') {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    try {
      // Destroy existing texture for this channel
      this.clearChannel(channel);

      // Create GPU texture
      const texture = this.device.createTexture({
        size: [bitmap.width, bitmap.height],
        format: 'rgba8unorm',
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST |
          GPUTextureUsage.RENDER_ATTACHMENT,
      });

      // Copy bitmap to texture
      this.device.queue.copyExternalImageToTexture(
        { source: bitmap },
        { texture },
        [bitmap.width, bitmap.height]
      );

      // Store texture info
      this.textures.set(channel, {
        texture,
        view: texture.createView(),
        source: sourceName,
        width: bitmap.width,
        height: bitmap.height,
      });

      // Notify change
      if (this.onTextureChange) {
        this.onTextureChange(channel, this.getChannelInfo(channel));
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: `Failed to create texture: ${error.message}` };
    }
  }

  /**
   * Generate a noise texture
   * @param {number} channel - Channel index
   * @param {number} size - Texture size (width = height)
   * @returns {{success: boolean, error?: string}}
   */
  generateNoiseTexture(channel = 0, size = 256) {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    const clampedSize = this.clampTextureSize(size);

    try {
      this.clearChannel(channel);

      const texture = this.device.createTexture({
        size: [clampedSize, clampedSize],
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      // Generate random noise data
      const data = new Uint8Array(clampedSize * clampedSize * 4);
      for (let i = 0; i < clampedSize * clampedSize; i++) {
        const value = Math.floor(Math.random() * 256);
        data[i * 4] = value;
        data[i * 4 + 1] = value;
        data[i * 4 + 2] = value;
        data[i * 4 + 3] = 255;
      }

      this.device.queue.writeTexture(
        { texture },
        data,
        { bytesPerRow: clampedSize * 4 },
        [clampedSize, clampedSize]
      );

      this.textures.set(channel, {
        texture,
        view: texture.createView(),
        source: 'noise',
        width: clampedSize,
        height: clampedSize,
      });

      if (this.onTextureChange) {
        this.onTextureChange(channel, this.getChannelInfo(channel));
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: `Failed to generate noise: ${error.message}` };
    }
  }

  /**
   * Generate a gradient texture (black to white, left to right)
   * @param {number} channel - Channel index
   * @param {number} size - Texture size (width = height)
   * @returns {{success: boolean, error?: string}}
   */
  generateGradientTexture(channel = 0, size = 256) {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    const clampedSize = this.clampTextureSize(size);

    try {
      this.clearChannel(channel);

      const texture = this.device.createTexture({
        size: [clampedSize, clampedSize],
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      // Generate linear gradient data (left to right, black to white)
      const data = new Uint8Array(clampedSize * clampedSize * 4);
      for (let y = 0; y < clampedSize; y++) {
        for (let x = 0; x < clampedSize; x++) {
          const i = (y * clampedSize + x) * 4;
          const value = Math.floor((x / (clampedSize - 1)) * 255);
          data[i] = value;
          data[i + 1] = value;
          data[i + 2] = value;
          data[i + 3] = 255;
        }
      }

      this.device.queue.writeTexture(
        { texture },
        data,
        { bytesPerRow: clampedSize * 4 },
        [clampedSize, clampedSize]
      );

      this.textures.set(channel, {
        texture,
        view: texture.createView(),
        source: 'gradient',
        width: clampedSize,
        height: clampedSize,
      });

      if (this.onTextureChange) {
        this.onTextureChange(channel, this.getChannelInfo(channel));
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: `Failed to generate gradient: ${error.message}` };
    }
  }

  /**
   * Generate a checker texture (8x8 black and white pattern)
   * @param {number} channel - Channel index
   * @param {number} size - Texture size (width = height)
   * @param {number} checkerSize - Size of each checker square
   * @returns {{success: boolean, error?: string}}
   */
  generateCheckerTexture(channel = 0, size = 256, checkerSize = 32) {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    const clampedSize = this.clampTextureSize(size);

    try {
      this.clearChannel(channel);

      const texture = this.device.createTexture({
        size: [clampedSize, clampedSize],
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      // Generate checker pattern data
      const data = new Uint8Array(clampedSize * clampedSize * 4);
      for (let y = 0; y < clampedSize; y++) {
        for (let x = 0; x < clampedSize; x++) {
          const i = (y * clampedSize + x) * 4;
          const checkerX = Math.floor(x / checkerSize);
          const checkerY = Math.floor(y / checkerSize);
          const isWhite = (checkerX + checkerY) % 2 === 0;
          const value = isWhite ? 255 : 0;
          data[i] = value;
          data[i + 1] = value;
          data[i + 2] = value;
          data[i + 3] = 255;
        }
      }

      this.device.queue.writeTexture(
        { texture },
        data,
        { bytesPerRow: clampedSize * 4 },
        [clampedSize, clampedSize]
      );

      this.textures.set(channel, {
        texture,
        view: texture.createView(),
        source: 'checker',
        width: clampedSize,
        height: clampedSize,
      });

      if (this.onTextureChange) {
        this.onTextureChange(channel, this.getChannelInfo(channel));
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: `Failed to generate checker: ${error.message}` };
    }
  }

  /**
   * Get iOS rotation angle needed for camera correction
   * iOS Safari's copyExternalImageToTexture doesn't respect video rotation metadata
   * @returns {number} Rotation angle in degrees (0, 90, 180, 270)
   */
  _getIOSRotationAngle() {
    // screen.orientation.angle: 0 = portrait, 90 = landscape-left, 180 = portrait-upside-down, 270 = landscape-right
    // window.orientation (deprecated): 0 = portrait, 90 = landscape-left, -90/270 = landscape-right, 180 = portrait-upside-down
    const angle = screen.orientation?.angle ?? window.orientation ?? 0;
    // Normalize negative values
    return ((angle % 360) + 360) % 360;
  }

  /**
   * Handle orientation change event for iOS
   */
  _handleOrientationChange() {
    const newAngle = this._getIOSRotationAngle();

    for (const [channel, camera] of this.cameras) {
      if (!camera.isIOS) continue;

      camera.rotation = newAngle;

      // Recreate rotation canvas and texture if dimensions change
      const { video } = camera;
      // Dimensions swap when rotating 90° (landscape modes)
      const needsRotation = newAngle === 90 || newAngle === 270;
      const texWidth = needsRotation ? video.videoHeight : video.videoWidth;
      const texHeight = needsRotation ? video.videoWidth : video.videoHeight;

      const texInfo = this.textures.get(channel);
      if (texInfo && (texInfo.width !== texWidth || texInfo.height !== texHeight)) {
        // Destroy old texture
        texInfo.texture.destroy();

        // Create new texture with correct dimensions
        const texture = this.device.createTexture({
          size: [texWidth, texHeight],
          format: 'rgba8unorm',
          usage:
            GPUTextureUsage.TEXTURE_BINDING |
            GPUTextureUsage.COPY_DST |
            GPUTextureUsage.RENDER_ATTACHMENT,
        });

        texInfo.texture = texture;
        texInfo.view = texture.createView();
        texInfo.width = texWidth;
        texInfo.height = texHeight;

        // Update rotation canvas
        if (camera.rotationCanvas) {
          camera.rotationCanvas.width = texWidth;
          camera.rotationCanvas.height = texHeight;
        }

        if (this.onTextureChange) {
          this.onTextureChange(channel, this.getChannelInfo(channel));
        }
      }
    }
  }

  /**
   * Start camera and bind to a channel
   * @param {number} channel - Channel index
   * @param {string} facingMode - 'user' (front) or 'environment' (back)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async startCamera(channel = 0, facingMode = 'user') {
    const channelCheck = this.validateChannel(channel);
    if (!channelCheck.valid) {
      return { success: false, error: channelCheck.error };
    }

    try {
      // Stop existing camera on this channel
      this.stopCamera(channel);
      this.clearChannel(channel);

      // Request camera access with timeout
      // Request 1080p ideal resolution
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Camera permission timeout')), CAMERA_TIMEOUT_MS)
        ),
      ]);

      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      // Wait for video to be ready with timeout
      await Promise.race([
        new Promise((resolve, reject) => {
          video.onloadedmetadata = () => {
            video.play().then(resolve).catch(reject);
          };
          video.onerror = reject;
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Video initialization timeout')), CAMERA_TIMEOUT_MS)
        ),
      ]);

      // iOS Safari rotation handling
      const isIOS = this.isIOS;
      let rotation = 0;
      let rotationCanvas = null;
      let rotationCtx = null;

      if (isIOS) {
        rotation = this._getIOSRotationAngle();

        // Create OffscreenCanvas for rotation
        // Note: Use regular canvas as fallback since OffscreenCanvas may not support 2d context in all browsers
        // Dimensions swap when rotating 90° (landscape modes)
        const needsDimensionSwap = rotation === 90 || rotation === 270;
        const canvasWidth = needsDimensionSwap ? video.videoHeight : video.videoWidth;
        const canvasHeight = needsDimensionSwap ? video.videoWidth : video.videoHeight;

        if (typeof OffscreenCanvas !== 'undefined') {
          rotationCanvas = new OffscreenCanvas(canvasWidth, canvasHeight);
        } else {
          rotationCanvas = document.createElement('canvas');
          rotationCanvas.width = canvasWidth;
          rotationCanvas.height = canvasHeight;
        }
        rotationCtx = rotationCanvas.getContext('2d');

        // Add orientation change listener (only once per first camera)
        if (this.cameras.size === 0) {
          window.addEventListener('orientationchange', this._onOrientationChange);
        }
      }

      // Calculate texture dimensions based on iOS rotation
      // Dimensions swap when rotating 90° (landscape modes)
      const needsRotation = isIOS && (rotation === 90 || rotation === 270);
      const texWidth = needsRotation ? video.videoHeight : video.videoWidth;
      const texHeight = needsRotation ? video.videoWidth : video.videoHeight;

      // Create texture matching dimensions (potentially swapped for iOS rotation)
      const texture = this.device.createTexture({
        size: [texWidth, texHeight],
        format: 'rgba8unorm',
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST |
          GPUTextureUsage.RENDER_ATTACHMENT,
      });

      // Store camera info with iOS rotation data
      this.cameras.set(channel, {
        video,
        stream,
        facingMode,
        isIOS,
        rotation,
        rotationCanvas,
        rotationCtx,
      });

      // Store texture info
      this.textures.set(channel, {
        texture,
        view: texture.createView(),
        source: `camera (${facingMode === 'user' ? 'front' : 'back'})`,
        width: texWidth,
        height: texHeight,
        isCamera: true,
      });

      if (this.onTextureChange) {
        this.onTextureChange(channel, this.getChannelInfo(channel));
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: `Camera access failed: ${error.message}` };
    }
  }

  /**
   * Stop camera for a channel
   * @param {number} channel
   */
  stopCamera(channel) {
    const camera = this.cameras.get(channel);
    if (camera) {
      // Stop all tracks
      camera.stream.getTracks().forEach((track) => track.stop());
      camera.video.srcObject = null;

      // Clean up rotation resources
      camera.rotationCanvas = null;
      camera.rotationCtx = null;

      this.cameras.delete(channel);

      // Remove orientation change listener if no cameras left
      if (this.cameras.size === 0) {
        window.removeEventListener('orientationchange', this._onOrientationChange);
      }
    }
  }

  /**
   * Update camera texture from video frame
   * Should be called each render frame
   * @param {number} channel
   */
  updateCameraTexture(channel) {
    const camera = this.cameras.get(channel);
    const texInfo = this.textures.get(channel);

    if (!camera || !texInfo || !texInfo.isCamera) return;

    const { video, isIOS, rotation, rotationCanvas, rotationCtx } = camera;
    if (video.readyState < video.HAVE_CURRENT_DATA) return;

    // iOS Safari: apply rotation correction via canvas
    // copyExternalImageToTexture ignores video rotation metadata on iOS
    if (isIOS && rotationCanvas && rotationCtx) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      // Dimensions swap when rotating 90° (landscape modes)
      const needsDimensionSwap = rotation === 90 || rotation === 270;
      const cw = needsDimensionSwap ? vh : vw;
      const ch = needsDimensionSwap ? vw : vh;

      // Update canvas size if needed
      if (rotationCanvas.width !== cw || rotationCanvas.height !== ch) {
        rotationCanvas.width = cw;
        rotationCanvas.height = ch;
      }

      rotationCtx.save();

      // Apply rotation transform based on device orientation
      // iOS video is captured in native sensor orientation (landscape)
      // copyExternalImageToTexture gets raw frames without rotation metadata applied
      // We need to rotate to match the current device orientation
      switch (rotation) {
        case 0:
          // Portrait: no rotation needed (video may already be correctly oriented)
          break;
        case 90:
          // Landscape-left: rotate 90° CCW
          rotationCtx.translate(0, ch);
          rotationCtx.rotate(-Math.PI / 2);
          break;
        case 180:
          // Portrait upside-down: rotate 180°
          rotationCtx.translate(cw, ch);
          rotationCtx.rotate(Math.PI);
          break;
        case 270:
          // Landscape-right: rotate 90° CW
          rotationCtx.translate(cw, 0);
          rotationCtx.rotate(Math.PI / 2);
          break;
      }

      rotationCtx.drawImage(video, 0, 0);
      rotationCtx.restore();

      // Copy rotated canvas to texture
      this.device.queue.copyExternalImageToTexture(
        { source: rotationCanvas },
        { texture: texInfo.texture },
        [cw, ch]
      );
    } else {
      // Non-iOS: copy video frame directly to texture
      this.device.queue.copyExternalImageToTexture(
        { source: video },
        { texture: texInfo.texture },
        [video.videoWidth, video.videoHeight]
      );
    }
  }

  /**
   * Update all active camera textures
   */
  updateAllCameraTextures() {
    for (const channel of this.cameras.keys()) {
      this.updateCameraTexture(channel);
    }
  }

  /**
   * Check if camera is active on a channel
   * @param {number} channel
   * @returns {boolean}
   */
  isCameraActive(channel) {
    return this.cameras.has(channel);
  }

  /**
   * Toggle camera facing mode
   * @param {number} channel
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async toggleCameraFacing(channel) {
    const camera = this.cameras.get(channel);
    if (!camera) {
      return { success: false, error: 'No camera active on this channel' };
    }

    const newFacingMode = camera.facingMode === 'user' ? 'environment' : 'user';
    return this.startCamera(channel, newFacingMode);
  }

  /**
   * Clear texture from a channel
   * @param {number} channel
   */
  clearChannel(channel) {
    // Stop camera if active
    this.stopCamera(channel);

    const existing = this.textures.get(channel);
    if (existing) {
      existing.texture.destroy();
      this.textures.delete(channel);

      if (this.onTextureChange) {
        this.onTextureChange(channel, null);
      }
    }
  }

  /**
   * Get texture view for a channel (returns default if not set)
   * @param {number} channel
   * @returns {GPUTextureView}
   */
  getTextureView(channel) {
    const info = this.textures.get(channel);
    return info ? info.view : this.defaultTextureView;
  }

  /**
   * Get the default dummy texture view (1x1 black pixel)
   * @returns {GPUTextureView}
   */
  getDummyTextureView() {
    return this.defaultTextureView;
  }

  /**
   * Get channel info
   * @param {number} channel
   * @returns {Object|null}
   */
  getChannelInfo(channel) {
    const info = this.textures.get(channel);
    if (!info) return null;
    return {
      source: info.source,
      width: info.width,
      height: info.height,
      isCamera: info.isCamera ?? false,
    };
  }

  /**
   * Check if channel has a texture
   * @param {number} channel
   * @returns {boolean}
   */
  hasTexture(channel) {
    return this.textures.has(channel);
  }

  /**
   * Get the shared sampler
   * @returns {GPUSampler}
   */
  getSampler() {
    return this.sampler;
  }

  /**
   * Destroy all resources
   */
  destroy() {
    // Remove orientation change listener
    window.removeEventListener('orientationchange', this._onOrientationChange);

    // Stop all cameras
    for (const channel of this.cameras.keys()) {
      this.stopCamera(channel);
    }
    this.cameras.clear();

    for (const [, info] of this.textures) {
      info.texture.destroy();
    }
    this.textures.clear();

    if (this.defaultTexture) {
      this.defaultTexture.destroy();
      this.defaultTexture = null;
    }

    this.sampler = null;
  }
}
